import { useRouter } from "next/router";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/v2/ui/button";

const Pagination = ({ nPages, currentPage, onPageChange, setCurrentPage }) => {
  const router = useRouter();

  const handlePageChange = (page) => {
    if (page < 1 || page > nPages || page === currentPage) return;
    
    // Update local state if provided
    const changeFn = onPageChange || setCurrentPage;
    if (changeFn) {
      changeFn(page);
    }
    
    // Update URL query param
    router.push({
      pathname: router.pathname,
      query: { ...router.query, page: page }
    }, undefined, { shallow: true });

    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages = [];
    const showMax = 5;

    if (nPages <= showMax) {
      for (let i = 1; i <= nPages; i++) pages.push(i);
    } else {
      // Logic for 1 ... 4 5 6 ... 10
      pages.push(1);
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(nPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = 4;
      } else if (currentPage >= nPages - 2) {
        start = nPages - 3;
      }

      if (start > 2) pages.push("ellipsis-1");
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < nPages - 1) pages.push("ellipsis-2");
      
      pages.push(nPages);
    }
    return pages;
  };

  if (nPages <= 1) return null;

  return (
    <nav className="flex items-center justify-center space-x-2 py-8" aria-label="Pagination">
      <Button
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border-primary/20 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
      >
        <ChevronLeft className="h-4 w-4" />
        <span>Previous</span>
      </Button>
      
      <Button
        variant="outline"
        size="icon"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="sm:hidden h-9 w-9 rounded-lg border-primary/20"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-1">
        {getPageNumbers().map((page, idx) => {
          if (typeof page === "string") {
            return (
              <div key={idx} className="w-9 h-9 flex items-center justify-center text-muted-foreground">
                <MoreHorizontal className="h-4 w-4" />
              </div>
            );
          }

          const isActive = currentPage === page;

          return (
            <Button
              key={idx}
              variant={isActive ? "default" : "outline"}
              size="icon"
              onClick={() => handlePageChange(page)}
              className={`h-9 w-9 rounded-lg transition-all duration-200 ${
                isActive 
                  ? "bg-primary text-primary-foreground shadow-md scale-105" 
                  : "border-primary/10 hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
              }`}
            >
              {page}
            </Button>
          );
        })}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === nPages}
        className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border-primary/20 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
      >
        <span>Next</span>
        <ChevronRight className="h-4 w-4" />
      </Button>

      <Button
        variant="outline"
        size="icon"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === nPages}
        className="sm:hidden h-9 w-9 rounded-lg border-primary/20"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </nav>
  );
};

export default Pagination;